import { useState, useCallback, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard } from 'lucide-react';
import { useIsDark } from '../../contexts/ThemeContext';
import { StripeNodeFormValues, StripeOperation } from '../../types/stripe';
import { baseStripeSchema, getSchemaForOperation } from '../../lib/stripe-validation';
import { STRIPE_OPERATION_INFO, DEFAULT_CURRENCY, DEFAULT_TIMEOUT_SEC } from '../../data/stripe-constants';
import { Step1Operation } from './stripe/Step1Operation';
import { Step2AccountBehavior } from './stripe/Step2AccountBehavior';
import { Step3Inputs } from './stripe/Step3Inputs';
import { Step5OutputMapping } from './stripe/Step5OutputMapping';

interface StripeConfigProps {
	data: {
		config?: StripeNodeFormValues;
		label?: string;
		description?: string;
		[key: string]: any;
	};
	onChange: (data: any) => void;
	nodeId: string;
	// Wizard state callbacks for parent component
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
		isComplete: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
	onClose?: () => void;
}

const STEPS = [
	{ key: 1, title: 'Operation', description: 'Choose Stripe operation' },
	{ key: 2, title: 'Account', description: 'Account settings' },
	{ key: 3, title: 'Inputs', description: 'Configure inputs' },
	{ key: 4, title: 'Output', description: 'Output mapping' },
];

export function StripeConfig({ data, onChange, nodeId, onWizardStateChange, onValidationRequest, onClose }: StripeConfigProps) {
	const [currentStep, setCurrentStep] = useState(1);
	const [validationAttempted, setValidationAttempted] = useState(false);
	const [validationFailed, setValidationFailed] = useState(false);
	const isDark = useIsDark();

	// Set default values if not present
	const defaultData: StripeNodeFormValues = {
		stripeAccountMode: 'platform',
		currency: DEFAULT_CURRENCY,
		timeoutSec: DEFAULT_TIMEOUT_SEC,
		waitForWebhook: false,
		operation: 'create_customer', // Provide a default operation
		...(data.config || {}),
	};

	const form = useForm<StripeNodeFormValues>({
		resolver: zodResolver(baseStripeSchema),
		defaultValues: defaultData,
	});

	const watchedOperation = form.watch('operation');
	const watchedConfig = form.watch();

	// Debounced onChange to reduce excessive auto layout triggers
	const debouncedOnChange = useCallback(
		useRef(
			debounce((newConfig: Partial<StripeNodeFormValues>) => {
				onChange({
					...data,
					config: {
						...(data.config || {}),
						...newConfig,
					},
				});
			}, 300)
		).current,
		[onChange, data]
	);

	// Handle label changes immediately without debouncing
	const handleLabelChange = useCallback((newLabel: string) => {
		// Update the label in the local data without triggering a full onChange
		// This prevents the node from closing when editing the label
		onChange({
			...data,
			label: newLabel,
		});
	}, [onChange, data]);

	useEffect(() => {
		// Update form when data changes externally
		const configData = data.config || {};
		Object.entries(configData).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== '') {
				// Type guard to ensure value is compatible with form.setValue
				if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || 
					(Array.isArray(value) && value.length > 0) || 
					(typeof value === 'object' && Object.keys(value).length > 0)) {
					form.setValue(key as keyof StripeNodeFormValues, value);
				}
			}
		});
	}, [data.config, form]);

	const handleNextStep = () => {
		if (currentStep < STEPS.length) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePreviousStep = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const canProceedToNext = () => {
		switch (currentStep) {
			case 1:
				return !!watchedOperation;
			case 2:
				// Account settings are optional, but if connect mode is selected, connectedAccountIdPath is required
				if (watchedConfig.stripeAccountMode === 'connect') {
					return !!watchedConfig.connectedAccountIdPath;
				}
				return true;
			case 3:
				// Check if all required fields for the selected operation are filled
				return validateOperationInputs(watchedOperation, watchedConfig);
			case 4:
				// Output mapping is the final step, check if all required fields are still valid
				return validateOperationInputs(watchedOperation, watchedConfig);
			default:
				return true;
		}
	};

	// Helper function to validate operation-specific required fields
	const validateOperationInputs = (operation: StripeOperation | undefined, config: StripeNodeFormValues): boolean => {
		if (!operation) return false;

		const operationInfo = STRIPE_OPERATION_INFO[operation];
		if (!operationInfo) return false;

		// Check if all required fields are filled
		for (const requiredField of operationInfo.requiredFields) {
			const fieldValue = config[requiredField as keyof StripeNodeFormValues];
			
			if (!fieldValue || typeof fieldValue !== 'string' || fieldValue.trim() === '') {
				return false;
			}
		}

		// Special validation for refund_payment (either paymentIntentIdPath or chargeIdPath must be provided)
		if (operation === 'refund_payment') {
			const hasPaymentIntent = !!(config.paymentIntentIdPath && config.paymentIntentIdPath.trim());
			const hasChargeId = !!(config.chargeIdPath && config.chargeIdPath.trim());
			
			if (!hasPaymentIntent && !hasChargeId) {
				return false;
			}
		}

		// Special validation for connect mode
		if (config.stripeAccountMode === 'connect' && !config.connectedAccountIdPath) {
			return false;
		}

		return true;
	};

	// Check if the entire node configuration is complete and valid
	const isNodeComplete = (): boolean => {
		if (!watchedOperation) {
			return false;
		}
		
		// Check if all required fields for the operation are filled
		if (!validateOperationInputs(watchedOperation, watchedConfig)) {
			return false;
		}

		// Additional checks for common required fields
		if (watchedConfig.stripeAccountMode === 'connect' && !watchedConfig.connectedAccountIdPath) {
			return false;
		}

		return true;
	};

	// Set up validation request handler
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(async () => {
				setValidationAttempted(true);
				const isValid = await form.trigger();
				setValidationFailed(!isValid);
				return isValid;
			});
		}
	}, [form, onValidationRequest]);

	// Update wizard state whenever configuration changes
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode: true,
				currentStep,
				totalSteps: STEPS.length,
				canProceedToNext: canProceedToNext(),
				onPreviousStep: handlePreviousStep,
				onNextStep: handleNextStep,
				onCompleteWizard: async () => {
					// Save the current configuration
					const formData = form.getValues();
					onChange({
						...data,
						config: formData,
					});
					
					// Close the drawer
					if (onClose) {
						onClose();
					}
				},
				validationAttempted,
				validationFailed,
				isComplete: isNodeComplete(),
			});
		}
	}, [watchedConfig, onWizardStateChange, currentStep, validationAttempted, validationFailed, form, onChange, onClose, data]);

	return (
		<div className="flex flex-col h-full max-h-full">
			{/* Step Content */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden p-6 bg-white transition-colors dark:bg-gray-900 min-h-0 h-full">
				<div className="max-w-none pb-8 space-y-6">
					{/* Header */}
					<div className="flex items-center space-x-3">
						<div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
							<CreditCard className="w-6 h-6 text-purple-600 dark:text-purple-400" />
						</div>
						<div>
							<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
								Stripe Node Configuration
							</h2>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								Configure your Stripe workflow node
							</p>
						</div>
					</div>

					{/* Current Operation Info */}
					{watchedOperation && (
						<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
							<div className="flex items-start space-x-3">
								<div className="flex-1">
									<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
										{STRIPE_OPERATION_INFO[watchedOperation]?.title}
									</h4>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										{STRIPE_OPERATION_INFO[watchedOperation]?.description}
									</p>
									{STRIPE_OPERATION_INFO[watchedOperation]?.requiredFields.length > 0 && (
										<div className="mt-2">
											<p className="text-xs text-gray-500 dark:text-gray-500">
												Required fields: {STRIPE_OPERATION_INFO[watchedOperation]?.requiredFields.join(', ')}
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Step Indicator */}
					<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
						<div className="flex items-center justify-center space-x-8">
							{STEPS.map((step) => {
								const isActive = step.key === currentStep;
								const isCompleted = currentStep > step.key;

								return (
									<div key={step.key} className="flex items-center space-x-2">
										<div
											className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
												isActive
													? 'bg-purple-600 text-white'
													: isCompleted
													? 'bg-green-600 text-white'
													: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
											}`}
										>
											{step.key}
										</div>
										<div className="text-center">
											<div
												className={`text-sm font-medium ${
													isActive
														? 'text-purple-600 dark:text-purple-400'
														: isCompleted
														? 'text-green-600 dark:text-green-400'
														: 'text-gray-500 dark:text-gray-400'
												}`}
											>
												{step.title}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					{/* Step Content */}
					<FormProvider {...form}>
						<AnimatePresence mode="wait">
							<motion.div
								key={currentStep}
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -20 }}
								transition={{ duration: 0.2 }}
							>
								{currentStep === 1 && (
									<Step1Operation
										config={form.watch()}
										onChange={(newConfig: Partial<StripeNodeFormValues>) => {
											Object.entries(newConfig).forEach(([key, value]) => {
												form.setValue(key as keyof StripeNodeFormValues, value);
											});
											debouncedOnChange(newConfig);
										}}
									/>
								)}
								{currentStep === 2 && (
									<Step2AccountBehavior
										config={form.watch()}
										onChange={(newConfig: Partial<StripeNodeFormValues>) => {
											Object.entries(newConfig).forEach(([key, value]) => {
												form.setValue(key as keyof StripeNodeFormValues, value);
											});
											debouncedOnChange(newConfig);
										}}
									/>
								)}
								{currentStep === 3 && (
									<Step3Inputs
										operation={watchedOperation}
										config={form.watch()}
										onChange={(newConfig: Partial<StripeNodeFormValues>) => {
											Object.entries(newConfig).forEach(([key, value]) => {
												form.setValue(key as keyof StripeNodeFormValues, value);
											});
											debouncedOnChange(newConfig);
										}}
										nodeId={nodeId}
									/>
								)}
								{currentStep === 4 && (
									<Step5OutputMapping
										operation={watchedOperation}
									/>
								)}
							</motion.div>
						</AnimatePresence>
					</FormProvider>

					{/* Validation Errors */}
					{validationAttempted && validationFailed && (
						<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
							<div className="flex items-center space-x-3">
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-red-600 dark:text-red-400">
									<path d="M18 6 6 18"/>
									<path d="m13 10-3 3 3 3"/>
								</svg>
								<span className="text-red-800 dark:text-red-200">
									Please fix the validation errors above before proceeding.
								</span>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

// Simple debounce utility
function debounce<T extends (...args: any[]) => any>(
	func: T,
	wait: number
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout;
	return (...args: Parameters<T>) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
}
